import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { default as Select, components } from 'react-select';
import Modal from 'react-modal';
import { AmountRange } from "./AmountRange";
import './AmountFilter.css';
import { formatDollarAmount, MIN_DISBURSEMENT_QUERY_AMOUNT, MAX_DISBURSEMENT_QUERY_AMOUNT } from '../../../util/EfupUtility';
import { validateAmountRange } from '../../../validators/AmountRangeValidator';
import { DropdownIndicator } from '../../CustomDropdownIndicator/CustomDropdownIndicator'
import { formatTestId } from '../../../util/EfupUtility';

const customSelectStyles = {
    menu: (provided) => ({
        ...provided,
        marginTop: '5px'
    }),
    option: (styles, { isFocused }) => {
        return {
            ...styles,
            backgroundColor: isFocused ? '#00b5e2' : undefined,
            color: '#333333'
        }
    },
    control: (styles, { isDisabled, isFocused, isSelected }) => {
        return {
            ...styles,
            boxShadow: isFocused ? '0 0 0 1px #00b5e2' : undefined,
            borderColor: isDisabled
                ? undefined
                : isSelected && !isFocused
                    ? '#00b5e2'
                    : isFocused
                        ? '#00b5e2'
                        : '#D3D6DE',
            ':hover': {
                borderColor: '#00b5e2',
            }
        }
    }
}

const customAmountRangeClasses = {
    container: 'efup-amount-range-input-container-border',
    inputSeparator: 'input-separator'
}

// Adds the "Amount: " label before the option that is selected
const Control = ({ children, ...props }) => {
    const label = props.selectProps.customProps.selectLabel;
    const isMobile = props.selectProps.customProps.isMobile;
    return (
        <components.Control {...props}>
            {isMobile?<></>:
                <div 
                    className={`efup-filter-label cb-font-poppins efup-amount-filter-label`}
                    data-testid="amount-filter-control-label"
                >{label}</div> 
            }
            <div className="efup-filter-value-container" data-testid="amount-filter-control-values">{children}</div>
        </components.Control>
    );
};

const Option = (props) => {
    const selectType = props.selectProps.customProps.selectType;
    const testId = formatTestId(`amount-filter-${props.label}-option`)

    return (
        <components.Option {...props} className={`efup-filter-option efup-${selectType}-dropdown-option`}>
            <div data-testid={testId}>{props.label}</div>
        </components.Option>
    );
};

export const AmountFilter = forwardRef((props, ref) => {

    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const minimumValue = props.min === MIN_DISBURSEMENT_QUERY_AMOUNT ? '' : props.min;
    const maximumValue = props.max === MAX_DISBURSEMENT_QUERY_AMOUNT ? '' : props.max;

    // Amounts we receive directly from AmountRange.js, unvalidated
    const [startAmount, setStartAmount] = useState(minimumValue);
    const [endAmount, setEndAmount] = useState(maximumValue);

    // Validated amounts that the user entered
    const [confirmedStartAmount, setConfirmedStartAmount] = useState(minimumValue)
    const [confirmedEndAmount, setConfirmedEndAmount] = useState(maximumValue)
    const [errors, setErrors] = useState();

    // allow a parent to clear this filter
    useImperativeHandle(ref, () => ({
        reset() {
            clear();
        }
    }))

    const selectOptions = [
        {label: 'All', value: 'all'},
        {label: 'Filter Amounts...', value: 'filter'},
        {label: buildAmountRangeString(), value: 'amount-range'}
    ]

    // default to "all" selected (the first option)
    const [selectedValue, setSelectedValue] = useState(selectOptions[0]);

    useEffect(() => {
        if (props.onChange) {
            if (!startAmount && !endAmount) {
                props.onChange(MIN_DISBURSEMENT_QUERY_AMOUNT, MAX_DISBURSEMENT_QUERY_AMOUNT)
            } else {
                props.onChange(startAmount, endAmount);
            }
        }
    }, [selectedValue, startAmount, endAmount]);     // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // If the incoming min/max values updated, we want to apply to both
        // start/end for unconfirmed and confirmed state variables
        if (props.min === MIN_DISBURSEMENT_QUERY_AMOUNT && props.max === MAX_DISBURSEMENT_QUERY_AMOUNT) {
            // Default to "All"
            clear();
        } else {
            setStartAmount(props.min);
            setEndAmount(props.max);
            setConfirmedStartAmount(props.min);
            setConfirmedEndAmount(props.max);
            //effect exists to mark the appropriate selected value
        }
        
    }, [props.min, props.max]);     // eslint-disable-line react-hooks/exhaustive-deps

    // When the user clicks "Ok" and the amounts are valid,
    // Then select the option to display the amount string with the confirmed values
    useEffect(() => {
        if (confirmedStartAmount) {
            setSelectedValue(selectOptions[2])
        }
    }, [confirmedStartAmount, confirmedEndAmount]);     // eslint-disable-line react-hooks/exhaustive-deps

    function handleSelectChange(option, optionAction) {
        if (optionAction.action === 'select-option') {
            if (option.value === 'filter') {
                setIsModalOpen(true)
                setStartAmount(confirmedStartAmount)
                setEndAmount(confirmedEndAmount)
            } else if (option.value === 'all') {
                clear();
            }
        }
    }

    function clear() {
        // Clear all amounts
        setStartAmount('')
        setEndAmount('')
        setConfirmedStartAmount('')
        setConfirmedEndAmount('')
        setSelectedValue(selectOptions[0])
    }

    function handleApplyFilter() {
        let validationErrors = validateAmountRangeInput(startAmount, endAmount)

        // If no validation errors, close the modal and display the amount range in the selection control
        if (validationErrors === '') {
            setConfirmedStartAmount(startAmount)
            setConfirmedEndAmount(endAmount)
            setIsModalOpen(false)
        }
    }

    function validateAmountRangeInput(start, end) {
        var validationErrors = validateAmountRange(start, end)
        setErrors(validationErrors)

        return validationErrors
    }

    function handleModalClose() {
        setErrors('')
        setIsModalOpen(false)
    }

    function buildAmountRangeString() {
        if (confirmedEndAmount && confirmedStartAmount !== confirmedEndAmount) {
            return formatDollarAmount(confirmedStartAmount) + " - " + formatDollarAmount(confirmedEndAmount)
        } else {
            return formatDollarAmount(confirmedStartAmount)
        }
    }

    return (
        <div className="amount-filter-container">
            <Select
                className="cb-multiselect cb-form"
                options={selectOptions}
                customProps={{ selectLabel: "Amount:", selectType: "amount", isMobile: props.isMobile }}
                isSearchable={false}
                components={{ 
                    IndicatorSeparator: () => null,
                    Control,
                    Option,
                    DropdownIndicator
                }}
                styles={customSelectStyles}
                // Allows us to display the amount range when filtered, but doesn't show the range as a selectable option  
                filterOption={(option) => option.value !== "amount-range"}
                value={selectedValue}
                onChange={handleSelectChange}
            />

            {/* TODO: determine if the modal should be broken out into a separate component */}
            <Modal
                isOpen={isModalOpen}
                overlayClassName="amount-range-popup-overlay"
                className="amount-range-popup"
                onRequestClose={handleModalClose}
                ariaHideApp={false}
            >
                <div className="efup-modal amount-range-popup-overlay" id="amountFilterModal">
                    <div className="cb-modal-dialog" role="document">
                        <div className="cb-modal-content">
                            <div className="cb-modal-header">
                                <h2 className="cb-modal-title" data-testid="amount-filter-filter-amounts-label">Filter Amounts</h2>
                                <i 
                                    className="cb-close cb-icon icon-close" 
                                    aria-hidden="true" 
                                    data-dismiss="cb-modal" 
                                    aria-label="Close" 
                                    onClick={handleModalClose}
                                    data-testid="amount-filter-close-modal" />
                            </div>
                            <div className="cb-modal-body">
                                <div className="cb-form-group">
                                    <label className="cb-form-control-label" htmlFor="amountRange">Amount</label>
                                    <AmountRange 
                                        startAmount={startAmount}
                                        endAmount={endAmount}
                                        onStartChange={setStartAmount}
                                        onEndChange={setEndAmount}
                                        customClasses={customAmountRangeClasses}
                                        validator={validateAmountRangeInput}/>
                                </div>
                            </div>
                            <div className="cb-modal-footer">
                                <p className="efup-amount-range-error" data-testid="efup-amount-range-error">{errors}</p>
                                {/* TODO: Add callback logic to send the start and end amounts to the parent components to implement filtering */}
                                <button
                                    type="button"
                                    style={{ float: 'right' }}
                                    className="cb-btn cb-btn-secondary"
                                    data-dismiss="cb-modal"
                                    onClick={handleApplyFilter}
                                    data-testid="amount-filter-apply-filter-button"
                                    >
                                    Ok
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
})